import { Button, Col, Divider, message, Modal, Row } from 'antd'
import { inject, observer } from 'mobx-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import AccessControlAction from '../../components/AccessControlAction'
import { EmptyText } from '../../components/Common/CellText'
import { StatusTag } from '../../components/Common/StatusTag'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PageTitle from '../../components/PageTitle'
import PopoverWatchTime from '../../components/PopoverWatchTime'
import {
  ACL_ACTION_TYPE,
  ASSIGNEE_TYPE,
  INTERNAL_ASSESSMENT_STATUS,
  TYPE_STATUS,
  VIEWLOG_DATE,
} from '../../constants'
import DashboardLayout from '../../layouts/DashboardLayout'
import InternalAssessmentRadio from './InternalAssessmentRadio'
import InternalAssessmentTable from './InternalAssessmentTable'
import InternalAssessmentUpdate from './InternalAssessmentUpdate/InternalAssessmentUpdate'

const InternalAssessmentDetail = props => {
  const { assessmentCode } = useParams()
  const {
    history,
    internalAssessmentStore,
    loadingAnimationStore,
    userStore,
    authenticationStore,
    selectPeopleStore,
    selectUserStore,
  } = props
  const { departmentWithUserListV2 } = selectPeopleStore
  const {
    fetchRatingDetails,
    ratingDetails,
    updateStatus,
  } = internalAssessmentStore
  const { userList } = userStore
  const { currentUser } = authenticationStore
  const [openCreateAssessment, setOpenCreateAssessment] = useState(false)
  const [evaluatorType, setEvaluatorType] = useState('self')
  const [localRatingDetails, setLocalRatingDetails] = useState(null)

  const countEditUser = useMemo(() => {
    return localRatingDetails?.details?.filter(
      item => item.editUser && item.editUser.trim() !== ''
    ).length
  }, [localRatingDetails])

  useEffect(() => {
    ;(async () => {
      await internalAssessmentStore.clearRatingDetails()
    })()
  }, [])

  useEffect(() => {
    if (authenticationStore.currentUser) {
      loadingAnimationStore.showSpinner(true)
      userStore.setFilter('userListStatus', true)
      userStore.setFilter(
        'companyCode',
        authenticationStore.currentCompanyCode()
      )
      userStore.getUserListV2().finally(() => {
        loadingAnimationStore.showSpinner(false)
      })
    }
    return () => {
      userStore.clearStore()
    }
  }, [authenticationStore.currentUser])

  useEffect(() => {
    const updateLastStatus = async code => {
      try {
        loadingAnimationStore.showSpinner(true)
        await updateStatus(code, {
          title: localRatingDetails.title,
          status: 'COMPLETED',
        })
        await getRatingDetails(assessmentCode)
      } catch (error) {
        console.error('Error fetching rating details:', error)
      }
      loadingAnimationStore.showSpinner(false)
    }

    if (countEditUser === 3) {
      updateLastStatus(assessmentCode)
    }
  }, [countEditUser])

  const getRatingDetails = async code => {
    try {
      loadingAnimationStore.showSpinner(true)
      const response = await fetchRatingDetails(code)
      setLocalRatingDetails(response.data)
    } catch (error) {
      console.error('Error fetching rating details:', error)
      history.push('/internal-assessment')
      message.error(error?.vi || 'Đã có lỗi xảy ra!!')
    }
    loadingAnimationStore.showSpinner(false)
  }

  useEffect(() => {
    ;(async () => {
      await getRatingDetails(assessmentCode)
    })()
  }, [assessmentCode])

  useEffect(() => {
    ;(async () => {
      try {
        await selectPeopleStore.getGroupList()
        await selectPeopleStore.getDepartmentWithUsersV2()
      } catch (err) {
        console.log(err)
      }
    })()
  }, [])

  const handleDeleteRating = async () => {
    try {
      await internalAssessmentStore.deleteRating(assessmentCode)
      history.push('/internal-assessment')
      message.success('Xóa đánh giá thành công!')
    } catch (err) {
      console.log(err)
    }
  }

  const handleOpenCreateAssessment = detailsIndex => {
    const evaluatorTypes = {
      0: 'self',
      1: 'manager',
      2: 'office',
      3: 'editor',
    }
    setEvaluatorType(evaluatorTypes[detailsIndex])
    setOpenCreateAssessment(true)
  }

  const nguoiTao = useMemo(
    () =>
      localRatingDetails?.assignees
        ?.filter(el => el.permission === 'CREATE')
        ?.reduce((unique, item) => {
          const isDuplicate = unique.some(
            el => el.assignee_code === item.assignee_code
          )
          return isDuplicate ? unique : [...unique, item]
        }, []),
    [localRatingDetails]
  )

  const truongPhongBan = useMemo(
    () =>
      localRatingDetails?.assignees
        ?.filter(el => el.permission === 'LEAD')
        ?.reduce((unique, item) => {
          const isDuplicate = unique.some(
            el => el.assignee_code === item.assignee_code
          )
          return isDuplicate ? unique : [...unique, item]
        }, []),
    [localRatingDetails]
  )

  const banBienTap = useMemo(
    () =>
      localRatingDetails?.assignees
        ?.filter(el => el.permission === 'PIC')
        ?.reduce((unique, item) => {
          const isDuplicate = unique.some(
            el => el.assignee_code === item.assignee_code
          )
          return isDuplicate ? unique : [...unique, item]
        }, []),
    [localRatingDetails]
  )

  const vanPhong = useMemo(
    () =>
      localRatingDetails?.assignees
        ?.filter(el => el.permission === 'COOR')
        ?.reduce((unique, item) => {
          const isDuplicate = unique.some(
            el => el.assignee_code === item.assignee_code
          )
          return isDuplicate ? unique : [...unique, item]
        }, []),
    [localRatingDetails]
  )

  const isUserInList = useCallback(
    (list, currentUser, departmentWithUserListV2) => {
      if (!list || !currentUser) return false
      return list.some(assignee => {
        if (assignee.assignee_type === 'DPT') {
          const department = departmentWithUserListV2?.find(
            dept => dept.code === assignee.assignee_code
          )
          return (
            department?.users?.some(
              user => user.user_name === currentUser.username
            ) || false
          )
        }
        return assignee.assignee_code === currentUser.username
      })
    },
    [departmentWithUserListV2]
  )

  const isManager = useMemo(
    () => isUserInList(truongPhongBan, currentUser, departmentWithUserListV2),
    [departmentWithUserListV2, currentUser, isUserInList, truongPhongBan]
  )
  const isSelf = useMemo(
    () => isUserInList(nguoiTao, currentUser, departmentWithUserListV2),
    [currentUser, departmentWithUserListV2, isUserInList, nguoiTao]
  )
  const isOffice = useMemo(
    () => isUserInList(vanPhong, currentUser, departmentWithUserListV2),
    [currentUser, departmentWithUserListV2, isUserInList, vanPhong]
  )
  const isEditorial = useMemo(
    () => isUserInList(banBienTap, currentUser, departmentWithUserListV2),
    [banBienTap, currentUser, departmentWithUserListV2, isUserInList]
  )

  // Render a single evaluation section with its edit button (if permitted)
  const renderEvaluationSection = (title, detailsIndex, ratingDetails) => {
    const permissionLists = [nguoiTao, truongPhongBan, vanPhong, banBienTap]
    const canEdit = () => {
      const currentUsername = currentUser?.username
      const status = ratingDetails?.status

      // Nếu toàn bộ phiếu đã COMPLETED thì không cho chỉnh sửa
      if (status === INTERNAL_ASSESSMENT_STATUS.COMPLETED) return false

      // Nếu đang ở phần Tự đánh giá (detailsIndex 0) và Trưởng phòng (detailsIndex 1) đã nhập đánh giá thì không cho chỉnh sửa
      if (detailsIndex === 0 && ratingDetails?.details[1]?.comments !== null) {
        return false
      }

      // Nếu đang ở phần Ban Biên Tập (detailsIndex 3)
      // và đánh giá của Trưởng phòng (detailsIndex 1) chưa được nhập thì không cho chỉnh sửa
      if (detailsIndex === 3 && ratingDetails?.details[1]?.comments === null) {
        return false
      }

      if (!permissionLists[detailsIndex]) return false

      // Lấy danh sách user và department được phân quyền
      const assignedUsers = permissionLists[detailsIndex]
        .filter(user => user.assignee_type === 'USER')
        .map(user => user.assignee_code)

      const assignedDepartments = permissionLists[detailsIndex]
        .filter(user => user.assignee_type === 'DPT')
        .map(user => user.assignee_code)

      // Nếu user hiện tại nằm trong danh sách người dùng được phân quyền
      if (assignedUsers.includes(currentUsername)) return true

      // Hoặc nếu user thuộc phòng ban nào đó được phân quyền
      return departmentWithUserListV2.some(
        dept =>
          assignedDepartments.includes(dept.code) &&
          dept.users.some(user => user.user_name === currentUsername)
      )
    }

    return (
      <div style={{ margin: '0 10px' }} key={detailsIndex}>
        <p style={{ fontWeight: 'bold', margin: 0 }}>
          {title}
          {canEdit() && (
            <AccessControlAction
              aclActionType={ACL_ACTION_TYPE.edit__INTERNAL_ASSESSMENT}>
              <Button
                type="primary"
                style={{
                  margin: '0 0 10px 15px',
                  borderRadius: '5px',
                  fontSize: '13px',
                }}
                onClick={() => handleOpenCreateAssessment(detailsIndex)}>
                Chỉnh sửa
              </Button>
            </AccessControlAction>
          )}
        </p>
        <strong style={{ margin: '0 20px' }}>
          +{' '}
          {detailsIndex === 0
            ? 'Tự đánh giá các việc đã làm được và những hạn chế chưa làm được'
            : detailsIndex === 1
            ? 'Trưởng phòng đánh giá'
            : detailsIndex === 2
            ? ''
            : 'Ban biên tập đánh giá'}
        </strong>
        <div
          dangerouslySetInnerHTML={{
            __html: ratingDetails?.details[detailsIndex]?.selfEvaluation,
          }}
          style={{ margin: '0 20px' }}
        />

        {detailsIndex === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <strong style={{ margin: '0 20px' }}>
              + Số điểm đánh giá: {ratingDetails?.details[2]?.disciplineScore}
            </strong>
            <span style={{ margin: '0 20px' }}>
              Số điểm bị trừ nếu có: {ratingDetails?.details[2]?.skillScore}
            </span>
          </div>
        )}

        <div
          style={{
            margin: '0 20px',
            flexDirection: 'column',
            display: 'flex',
            gap: '10px',
          }}>
          <strong>
            {detailsIndex === 0
              ? '+ Tự xếp loại'
              : detailsIndex === 1
              ? '+ Trưởng phòng xếp loại'
              : detailsIndex === 2
              ? null
              : '+ Ban biên tập xếp loại'}
          </strong>
          {detailsIndex !== 2 ? (
            <InternalAssessmentRadio
              defaultSelectedValue={
                ratingDetails?.details[detailsIndex]?.comments || ''
              }
            />
          ) : null}
        </div>

        <Divider />
      </div>
    )
  }

  const renderEvaluations = () => {
    if (isManager || isEditorial || isSelf) {
      // Trưởng ban và Ban biên tập: hiển thị tất cả các ý kiến
      return (
        <>
          {renderEvaluationSection(
            'I. CÁ NHÂN TỰ XẾP LOẠI MỨC ĐỘ HOÀN THÀNH NHIỆM VỤ',
            0,
            localRatingDetails
          )}
          {renderEvaluationSection(
            'II. LÃNH ĐẠO PHÒNG BAN XẾP LOẠI MỨC ĐỘ HOÀN THÀNH NHIỆM VỤ',
            1,
            localRatingDetails
          )}
          {/* {renderEvaluationSection('III. Ý KIẾN VĂN PHÒNG', 2, localRatingDetails)} */}
          {renderEvaluationSection(
            'III. BAN BIÊN TẬP XẾP LOẠI MỨC ĐỘ HOÀN THÀNH NHIỆM VỤ',
            3,
            localRatingDetails
          )}
        </>
      )
    } else if (isOffice) {
      // Văn phòng: chỉ hiển thị ý kiến của cá nhân và văn phòng
      return (
        <>
          {renderEvaluationSection(
            'I. CÁ NHÂN TỰ XẾP LOẠI MỨC ĐỘ HOÀN THÀNH NHIỆM VỤ',
            0,
            localRatingDetails
          )}
          {/* {renderEvaluationSection('III. Ý KIẾN VĂN PHÒNG', 2, localRatingDetails)} */}
        </>
      )
    } else {
      // Trường hợp dự phòng: hiển thị ý kiến của cá nhân
      return (
        <>
          {renderEvaluationSection(
            'I. CÁ NHÂN TỰ XẾP LOẠI MỨC ĐỘ HOÀN THÀNH NHIỆM VỤ',
            0,
            localRatingDetails
          )}
        </>
      )
    }
  }

  const renderSection = ({
    title,
    userList,
    filterCondition,
    departWithUsers,
    selectUserStore,
  }) => {
    const filteredUsers = userList?.filter(filterCondition)
    const formattedUsers = selectUserStore?.convertListToDefault(filteredUsers)

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
        }}>
        <div style={{ display: 'flex', flexGrow: 1 }}>
          <b style={{ marginRight: '35px' }}>{title}:</b>
          {filteredUsers?.length !== 0 ? (
            <PopoverWatchTime
              departWithUsers={departWithUsers}
              userList={formattedUsers}
              title={title}
              isFullViewer={true}
              compared_date={VIEWLOG_DATE.internal_document_until}
              typeId={assessmentCode}
              created_at={localRatingDetails?.createdAt}
            />
          ) : (
            <EmptyText>Không có {title}.</EmptyText>
          )}
        </div>
      </div>
    )
  }

  // Augment each assignee with uppercase names from departments or users.
  localRatingDetails?.assignees?.forEach(assignee => {
    const matchingData = departmentWithUserListV2?.find(
      item => item.code === assignee.assignee_code
    )
    if (matchingData) {
      assignee.name_uppercase = matchingData.name
    }
  })

  localRatingDetails?.assignees?.forEach(el => {
    const user =
      userList && userList?.find(user => user.username === el.assignee_code)
    if (user) {
      el.name_uppercase = user.name_uppercase
    }
  })

  return (
    <>
      <DashboardLayout>
        <Helmet>
          <title>Đánh giá nội bộ | VIMC Đánh giá nội bộ VPRESS</title>
        </Helmet>
        <PageTitle
          location={props.location}
          showTitle={true}
          title={localRatingDetails?.title}
          status={StatusTag(
            TYPE_STATUS.INTERNAL_ASSESSMENT,
            localRatingDetails?.status
          )}
        />
        <ContentBlockWrapper>
          <Row>
            <Col span={12}>
              {renderSection({
                title: 'Trưởng phòng/ban',
                userList: truongPhongBan,
                filterCondition: user =>
                  user.permission === ASSIGNEE_TYPE.LEADER &&
                  user.name_uppercase != null,
                departWithUsers: departmentWithUserListV2,
                selectUserStore,
                comparedDate: VIEWLOG_DATE.internal_document_until,
                typeId: assessmentCode,
                createdAt: localRatingDetails?.createdAt,
              })}
              {/* {renderSection({
                title: 'Văn phòng',
                userList: vanPhong,
                filterCondition: user =>
                  user.permission === ASSIGNEE_TYPE.COMBINER &&
                  user.name_uppercase != null,
                departWithUsers: departmentWithUserListV2,
                selectUserStore,
                comparedDate: VIEWLOG_DATE.internal_document_until,
                typeId: assessmentCode,
                createdAt: ratingDetails?.createdAt,
              })} */}
              {renderSection({
                title: 'Ban biên tập',
                userList: banBienTap,
                filterCondition: user =>
                  user.permission === ASSIGNEE_TYPE.HANDLER &&
                  user.name_uppercase != null,
                departWithUsers: departmentWithUserListV2,
                selectUserStore,
                comparedDate: VIEWLOG_DATE.internal_document_until,
                typeId: assessmentCode,
                createdAt: localRatingDetails?.createdAt,
              })}
            </Col>
            <Col span={12}>
              <div
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {localRatingDetails?.status !==
                  INTERNAL_ASSESSMENT_STATUS.BBT_APPROVED &&
                  localRatingDetails?.status !==
                    INTERNAL_ASSESSMENT_STATUS.COMPLETED &&
                  currentUser?.username ===
                    localRatingDetails?.evaluateeName && (
                    <Button
                      type="danger"
                      style={{
                        borderRadius: '5px',
                        fontSize: '13px',
                        padding: '0 10px',
                      }}
                      onClick={() =>
                        Modal.confirm({
                          title:
                            'Bạn có chắc chắn muốn xóa phiếu đánh giá nội bộ này?',
                          onOk: handleDeleteRating,
                          okText: 'Đồng ý',
                          cancelText: 'Hủy bỏ',
                          okType: 'danger',
                        })
                      }>
                      Xóa đánh giá
                    </Button>
                  )}
                {/* <AccessControlAction
                  aclActionType={ACL_ACTION_TYPE.delete__INTERNAL_ASSESSMENT}> */}
                {/* </AccessControlAction> */}
              </div>
            </Col>
          </Row>
          <Divider />
          <Col>
            <h3 style={{ color: '#CA1010', margin: '0', padding: '10px 0' }}>
              ĐÁNH GIÁ XẾP LOẠI CB,VC,NLĐ THÁNG
            </h3>
          </Col>
          <Col style={{ marginBottom: '10px' }}>
            <h3 style={{ fontWeight: 'bold', margin: 0 }}>
              Tháng:{' '}
              <span style={{ marginRight: 24 }}>
                {localRatingDetails?.month}
              </span>
              Năm: <span>{localRatingDetails?.year}</span>
            </h3>
          </Col>
          <InternalAssessmentTable ratingDetails={localRatingDetails} />
          <Divider />
          <div style={{ marginTop: '10px' }}>{renderEvaluations()}</div>
        </ContentBlockWrapper>
      </DashboardLayout>
      {openCreateAssessment && (
        <InternalAssessmentUpdate
          open={openCreateAssessment}
          setOpen={setOpenCreateAssessment}
          code={assessmentCode}
          evaluatorType={evaluatorType}
          ratingDetails={localRatingDetails}
          getRatingDetails={getRatingDetails}
        />
      )}
    </>
  )
}

export default inject(
  'internalAssessmentStore',
  'loadingAnimationStore',
  'userStore',
  'authenticationStore',
  'selectUserStore',
  'selectPeopleStore'
)(observer(InternalAssessmentDetail))
